const AUTH_STORAGE_KEY = "spiritual-profile-platform-auth";
const normalizeApiBase = (value) => {
  if (!value) return null;
  const trimmed = value.trim().replace(/\/$/, "");
  return trimmed.endsWith("/api") ? trimmed : `${trimmed}/api`;
};

const API_BASE_CANDIDATES = [
  process.env.REACT_APP_API_BASE_URL,
  "http://localhost:8000/api",
  "http://127.0.0.1:8000/api",
]
  .map(normalizeApiBase)
  .filter(Boolean)
  .filter((value, index, values) => values.indexOf(value) === index);

let activeApiBase = API_BASE_CANDIDATES[0];

const defaultProfile = {
  fullName: "",
  email: "",
};

const clone = (value) => JSON.parse(JSON.stringify(value));

const withQueryParam = (path, key, value) => {
  if (!value) return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
};

const readSession = () => {
  try {
    const raw = window.sessionStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return { token: null, user: null };
    const parsed = JSON.parse(raw);
    return {
      token: parsed?.token || null,
      user: parsed?.user || null,
    };
  } catch {
    return { token: null, user: null };
  }
};

const writeSession = (session) => {
  window.sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
};

export const clearSession = () => {
  window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
};

const getToken = () => readSession().token;

const toError = async (response) => {
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  const detail = payload?.detail;
  const message =
    detail?.message ||
    (typeof detail === "string" ? detail : null) ||
    payload?.message ||
    `Request failed with status ${response.status}`;

  const error = new Error(message);
  error.status = response.status;
  error.payload = payload;
  return error;
};

const buildHeaders = (extra = {}) => {
  const headers = { ...extra };
  if (!headers.Authorization) {
    const token = getToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }
  return headers;
};

const requestJson = async (apiBase, path, options = {}) => {
  const response = await fetch(`${apiBase}${path}`, {
    ...options,
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      ...buildHeaders(options.headers || {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (!response.ok) {
    const error = await toError(response);
    if (response.status === 401) {
      clearSession();
    }
    throw error;
  }

  if (response.status === 204) {
    return null;
  }

  activeApiBase = apiBase;
  return response.json();
};

const parseFileName = (response) => {
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^";]+)"?/i);
  return match ? match[1] : null;
};

const requestBlob = async (apiBase, path) => {
  const response = await fetch(`${apiBase}${path}`, {
    cache: "no-store",
    headers: buildHeaders(),
  });
  if (!response.ok) {
    const error = await toError(response);
    if (response.status === 401) {
      clearSession();
    }
    throw error;
  }
  activeApiBase = apiBase;
  const fileName = parseFileName(response);
  const blob = await response.blob();
  return { blob, fileName };
};

const requestForm = async (apiBase, path, formData) => {
  const response = await fetch(`${apiBase}${path}`, {
    method: "POST",
    headers: buildHeaders(),
    body: formData,
  });

  if (!response.ok) {
    const error = await toError(response);
    if (response.status === 401) {
      clearSession();
    }
    throw error;
  }

  activeApiBase = apiBase;
  return response.json();
};

const apiFetch = async (path, options = {}) => {
  const apiBases = [activeApiBase, ...API_BASE_CANDIDATES.filter((base) => base !== activeApiBase)];
  let lastError = null;

  for (const apiBase of apiBases) {
    try {
      return await requestJson(apiBase, path, options);
    } catch (error) {
      const isNetworkError = !("status" in error);
      const isMissingRoute = error?.status === 404 && path === "/profiles";
      const isServerError = typeof error?.status === "number" && error.status >= 500;
      if (!isNetworkError && !isMissingRoute && !isServerError) {
        throw error;
      }
      lastError = error;
    }
  }

  throw lastError || new Error("Unable to connect to the profile API.");
};

const apiFetchBlob = async (path) => {
  const apiBases = [activeApiBase, ...API_BASE_CANDIDATES.filter((base) => base !== activeApiBase)];
  let lastError = null;

  for (const apiBase of apiBases) {
    try {
      return await requestBlob(apiBase, path);
    } catch (error) {
      const isNetworkError = !("status" in error);
      const isServerError = typeof error?.status === "number" && error.status >= 500;
      if (!isNetworkError && !isServerError) {
        throw error;
      }
      lastError = error;
    }
  }

  throw lastError || new Error("Unable to connect to the profile API.");
};

const apiFetchForm = async (path, formData) => {
  const apiBases = [activeApiBase, ...API_BASE_CANDIDATES.filter((base) => base !== activeApiBase)];
  let lastError = null;

  for (const apiBase of apiBases) {
    try {
      return await requestForm(apiBase, path, formData);
    } catch (error) {
      const isNetworkError = !("status" in error);
      const isMissingRoute = error?.status === 404;
      const isServerError = typeof error?.status === "number" && error.status >= 500;
      if (!isNetworkError && !isMissingRoute && !isServerError) {
        throw error;
      }
      lastError = error;
    }
  }

  throw lastError || new Error("Unable to connect to the profile API.");
};

const sortByNewest = (items = []) =>
  [...items].sort(
    (left, right) =>
      new Date(right.updatedAt || right.startedAt || 0).getTime() -
      new Date(left.updatedAt || left.startedAt || 0).getTime(),
  );

const normalizeProfileOverview = (overview) => {
  const attempts = sortByNewest(overview.attempts || []);
  const completedAttempts = attempts.filter((attempt) => attempt.status === "completed");
  const activeAttempt =
    "activeAttempt" in overview
      ? overview.activeAttempt
      : attempts.find((attempt) => attempt.status === "in_progress" || attempt.status === "paused") || null;
  return {
    ...overview,
    attempts,
    completedAttempts,
    activeAttempt,
    latestCompletedAttempt: overview.latestCompletedAttempt || completedAttempts[0] || null,
    cooldown: overview.cooldown || { blocked: false, availableAt: null },
  };
};

const ensureQuestionStats = (attempt, itemId) => {
  const stats = attempt.analytics.questionStats;
  if (!stats[itemId]) {
    stats[itemId] = {
      visitLogs: [],
      answerChanges: [],
    };
  }
  return stats[itemId];
};

const formatDateParts = (dateValue) => {
  const date = new Date(dateValue);
  return {
    day: date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    time: date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }),
  };
};

export const formatTimestamp = (dateValue) => {
  if (!dateValue) return "Not recorded";
  const { day, time } = formatDateParts(dateValue);
  return `${time} · ${day}`;
};

export const formatDuration = (durationMs = 0) => {
  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
};

export const getProfile = () => readSession().user || defaultProfile;
export const isAdminProfile = () => getProfile()?.role === "admin";

// True when this tab already holds an SPP session that the server still accepts (used inside the mobile app,
// where each page load would otherwise need the app's token again).
export const verifyStoredSession = async () => {
  if (!getToken()) return false;
  try {
    await apiFetch("/me");
    return true;
  } catch {
    return false;
  }
};

export const completeSsoLogin = async (keycloakToken) => {
  const result = await apiFetch("/auth/session", {
    method: "POST",
    headers: { Authorization: `Bearer ${keycloakToken}` },
  });
  writeSession({ token: result.access_token, user: result.user });
  return result.user;
};

export const fetchCurrentProfile = async () => {
  const user = await apiFetch("/me");
  writeSession({ token: getToken(), user });
  return user;
};

export const listProfiles = async () => {
  const result = await apiFetch("/profiles");
  return result.profiles || [];
};

export const getProfileDefinition = async (profileId, inviteToken = null) => {
  const result = await apiFetch(withQueryParam(`/profiles/${profileId}`, "invite", inviteToken));
  return result.profile;
};

export const getProfileItems = async (profileId, inviteToken = null) => {
  const result = await apiFetch(withQueryParam(`/profiles/${profileId}/items`, "invite", inviteToken));
  return result.items || [];
};

export const getProfileBundle = async (profileId, inviteToken = null) => {
  const [profile, items] = await Promise.all([
    getProfileDefinition(profileId, inviteToken),
    getProfileItems(profileId, inviteToken),
  ]);
  return { profile, items };
};

export const getDashboardOverview = async () => apiFetch("/me/dashboard");

export const getProfileOverview = async (profileId, inviteToken = null, attemptId = null) => {
  const path = withQueryParam(withQueryParam(`/me/profiles/${profileId}`, "invite", inviteToken), "attempt", attemptId);
  const overview = await apiFetch(path);
  return normalizeProfileOverview(overview);
};

export const getAttemptById = async (profileId, attemptId) =>
  apiFetch(`/me/profiles/${profileId}/attempts/${attemptId}`);

export const getLatestCompletedAttempt = async (profileId) => {
  const overview = await getProfileOverview(profileId);
  return overview.latestCompletedAttempt || null;
};

export const getAttemptReport = async (profileId, attemptId) =>
  apiFetch(`/me/profiles/${profileId}/attempts/${attemptId}/report`);

export const downloadAttemptPdf = async (profileId, attemptId) => {
  const fresh = Date.now();
  const { blob, fileName } = await apiFetchBlob(`/me/profiles/${profileId}/attempts/${attemptId}/report.pdf?fresh=${fresh}`);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName || `${profileId}-report-${attemptId}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadAttemptResponses = async (profileId, attemptId) => {
  const fresh = Date.now();
  const { blob, fileName } = await apiFetchBlob(`/me/profiles/${profileId}/attempts/${attemptId}/responses.csv?fresh=${fresh}`);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName || `${profileId}-responses-${attemptId}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const getAdminOverview = async () => apiFetch("/admin/overview");
export const getAdminUsers = async () => apiFetch("/admin/users");

export const getAdminUserAttempts = async (userId) => apiFetch(`/admin/users/${userId}/attempts`);

export const getAdminAttemptReport = async (userId, attemptId) =>
  apiFetch(`/admin/users/${userId}/attempts/${attemptId}/report`);

export const downloadAdminAttemptPdf = async (userId, attemptId) => {
  const fresh = Date.now();
  const { blob, fileName } = await apiFetchBlob(`/admin/users/${userId}/attempts/${attemptId}/report.pdf?fresh=${fresh}`);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName || `${userId}-report-${attemptId}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadAdminAttemptResponses = async (userId, attemptId) => {
  const fresh = Date.now();
  const { blob, fileName } = await apiFetchBlob(`/admin/users/${userId}/attempts/${attemptId}/responses.csv?fresh=${fresh}`);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName || `${userId}-responses-${attemptId}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
export const getAdminProfiles = async () => {
  const result = await apiFetch("/admin/profiles");
  return result.profiles || [];
};
export const getAdminProfileEditor = async (profileId) => {
  const result = await apiFetch(`/admin/profiles/${profileId}`);
  return result.profile;
};
export const importAdminProfileFromPdf = async ({ profilePdf, scoringPdf }) => {
  const formData = new FormData();
  if (profilePdf) {
    formData.append("profile_pdf", profilePdf);
  }
  if (scoringPdf) {
    formData.append("scoring_pdf", scoringPdf);
  }
  return apiFetchForm("/admin/profiles/import-pdf", formData);
};
export const createAdminProfile = async (payload) => {
  const result = await apiFetch("/admin/profiles", {
    method: "POST",
    body: payload,
  });
  return result.profile;
};
export const updateAdminProfile = async (profileId, payload) => {
  const result = await apiFetch(`/admin/profiles/${profileId}`, {
    method: "PUT",
    body: payload,
  });
  return result.profile;
};

export const beginAttempt = async (profileId, inviteToken = null) =>
  apiFetch(withQueryParam(`/me/profiles/${profileId}/begin`, "invite", inviteToken), { method: "POST" });

const persistPayload = (attempt) => ({
  status: attempt.status,
  currentQuestionId: attempt.currentQuestionId,
  answers: attempt.answers,
  skippedIds: attempt.skippedIds,
  analytics: attempt.analytics,
});

export const saveAttempt = async (profileId, attempt) =>
  apiFetch(`/me/profiles/${profileId}/attempts/${attempt.id}`, {
    method: "PATCH",
    body: persistPayload(attempt),
  });

export const enterQuestion = (attemptInput, itemId, timestamp = Date.now()) => {
  const attempt = clone(attemptInput);
  if (attempt.analytics.activeQuestionId && attempt.analytics.activeQuestionId !== itemId) {
    finalizeCurrentVisit(attempt, timestamp);
  }
  attempt.currentQuestionId = itemId;
  attempt.updatedAt = new Date(timestamp).toISOString();
  attempt.analytics.activeQuestionId = itemId;
  attempt.analytics.questionEnteredAt = timestamp;
  ensureQuestionStats(attempt, itemId);
  return attempt;
};

const finalizeCurrentVisit = (attempt, timestamp) => {
  const activeQuestionId = attempt.analytics.activeQuestionId;
  const enteredAt = attempt.analytics.questionEnteredAt;
  if (!activeQuestionId || !enteredAt) return attempt;

  const visitDurationMs = Math.max(0, timestamp - enteredAt);
  const questionStats = ensureQuestionStats(attempt, activeQuestionId);
  questionStats.visitLogs.push({
    enteredAt: new Date(enteredAt).toISOString(),
    leftAt: new Date(timestamp).toISOString(),
    durationMs: visitDurationMs,
    durationLabel: formatDuration(visitDurationMs),
  });
  attempt.analytics.totalActiveMs += visitDurationMs;
  attempt.analytics.activeQuestionId = null;
  attempt.analytics.questionEnteredAt = null;
  return attempt;
};

export const recordAnswer = (attemptInput, itemId, value, timestamp = Date.now()) => {
  const attempt = clone(attemptInput);
  const questionStats = ensureQuestionStats(attempt, itemId);
  const previousValue = attempt.answers[itemId];
  if (previousValue !== undefined && previousValue !== value) {
    questionStats.answerChanges.push({
      from: previousValue,
      to: value,
      changedAt: new Date(timestamp).toISOString(),
      dwellMs:
        attempt.analytics.activeQuestionId === itemId && attempt.analytics.questionEnteredAt
          ? Math.max(0, timestamp - attempt.analytics.questionEnteredAt)
          : 0,
    });
  }
  attempt.answers[itemId] = value;
  attempt.skippedIds = attempt.skippedIds.filter((id) => id !== itemId);
  attempt.updatedAt = new Date(timestamp).toISOString();
  return attempt;
};

export const toggleSkippedQuestion = (attemptInput, itemId) => {
  const attempt = clone(attemptInput);
  const skipped = new Set(attempt.skippedIds);
  if (skipped.has(itemId)) {
    skipped.delete(itemId);
  } else {
    skipped.add(itemId);
  }
  attempt.skippedIds = [...skipped].sort((left, right) => left - right);
  attempt.updatedAt = new Date().toISOString();
  return attempt;
};

export const pauseAttempt = (attemptInput, timestamp = Date.now()) => {
  const attempt = clone(attemptInput);
  finalizeCurrentVisit(attempt, timestamp);
  attempt.status = "paused";
  attempt.updatedAt = new Date(timestamp).toISOString();
  return attempt;
};

export const resumeAttempt = (attemptInput, timestamp = Date.now()) => {
  const attempt = clone(attemptInput);
  attempt.status = "in_progress";
  attempt.updatedAt = new Date(timestamp).toISOString();
  attempt.analytics.activeQuestionId = attempt.currentQuestionId;
  attempt.analytics.questionEnteredAt = timestamp;
  return attempt;
};

export const completeAttempt = async (profileId, attempt) =>
  apiFetch(`/me/profiles/${profileId}/attempts/${attempt.id}/complete`, {
    method: "POST",
  });

export const answeredCount = (attempt) => Object.keys(attempt?.answers || {}).length;

export const completionPercent = (attempt, itemCount = null) => {
  const total = itemCount || attempt?.questionCount || 0;
  if (!total) return 0;
  return Number(((answeredCount(attempt) / total) * 100).toFixed(1));
};

export const unansweredQuestionIds = (attempt, items = []) =>
  items.filter((item) => attempt?.answers?.[item.id] === undefined).map((item) => item.id);
